import { FormFieldType, FormSchemaEntry } from './types';
import * as validator from '@gik/core/utils/validator';
import i18next from '@gik/i18n';

export type ValidationErrorType =
  | 'singleRequired'
  | 'custom'
  | 'weekday-selector'
  | 'yes-no'
  | 'number'
  | 'minLength'
  | 'maxLength'
  | 'minValue'
  | 'maxValue'
  | 'required'
  | FormFieldType; // TODO: review: FormFieldType should not be a validation error type? same with yes-no and weekday-selector

export interface ValidationError {
  type?: ValidationErrorType; // type of validation error is used internally to determine what to do with certain validations
  message: string;
  params?: ITranslationParams;
  errors?: ValidationErrors;
  trackingId?: string;
}

export interface ITranslationParams {
  [key: string]: string;
}

export interface ValidationValues {
  [key: string]: string | number | boolean;
}

export interface ValidationErrors {
  // Note: the key is the name of the field to be validated
  [key: string]: ValidationError;
}

export interface ValidationOptions {
  formEl: HTMLFormElement;
  errors?: ValidationErrors; // existing errors
}

export function validateForm(
  schema: FormSchemaEntry[],
  // allow use of any here since the value really could be anything
  // eslint-disable-next-line
  values: Record<string, any>,
  options?: ValidationOptions
): ValidationErrors {
  if (!schema) return options?.errors;

  const errors = options?.errors || {};

  // loop though all entries
  schema.forEach(entry => {
    if (!entry) {
      throw new Error('schema entry is empty while trying to validate');
    }
    const key = entry.name;
    let error: ValidationError;

    // get the form value for this entry
    const value = values ? values[key] : null;

    // validate required fields
    if (entry.required) {
      const reqError: ValidationError = {
        message: entry.requiredMessage || 'required',
        params: { name: entry.errorName || entry.label || entry.placeholder || entry.name },
        type: 'required',
      };

      // specific validation based on field type
      if (entry.type === 'weekday-selector') {
        // the weekday selector is an array that always has values
        // it is considered empty when all values are false
        if (!value || !value.find((item: boolean) => item === true)) {
          error = reqError;
        }
      }

      switch (true) {
        case Array.isArray(value):
          if (value.length === 0) {
            error = reqError;
          }
          break;
        case typeof value === 'boolean':
          if (entry.type === 'yes-no') {
            error = value !== undefined ? undefined : reqError;
          } else {
            error = value ? undefined : reqError;
          }
          break;
        default:
          if (validator.isEmpty(value)) {
            switch (true) {
              default:
                error = reqError;
            }
          }
      }
    }

    // validate min length
    if (!error && entry.minLength !== undefined && entry.minLength !== null) {
      let msg = 'minLength';
      if (entry.type === 'number') {
        msg = 'minLengthNumber';
      }

      error = validator.minLength(value as string, entry.minLength)
        ? { message: msg, params: { count: entry.minLength.toString() } }
        : undefined;

      if (error) {
        error.type = 'minLength';
      }
    }

    // validate max length
    if (!error && entry.maxLength !== undefined && entry.maxLength !== null) {
      let msg = 'maxLength';
      if (entry.type === 'number') {
        msg = 'maxLengthNumber';
      }

      error = validator.maxLength(value as string, entry.maxLength)
        ? { message: msg, params: { count: entry.maxLength.toString() } }
        : undefined;

      if (error) {
        error.type = 'maxLength';
      }
    }

    // validate min value
    if (!error && entry.minValue !== undefined && entry.minValue !== null) {
      error = validator.minValue(value as number, entry.minValue)
        ? undefined
        : { message: 'minValue', params: { count: entry.minValue.toString() } };

      if (error) {
        error.type = 'minValue';
      }
    }

    // validate max value
    if (!error && entry.maxValue !== undefined && entry.maxValue !== null) {
      error = validator.maxValue(value as number, entry.maxValue)
        ? undefined
        : { message: 'maxValue', params: { count: entry.maxValue.toString() } };

      if (error) {
        error.type = 'maxValue';
      }
    }

    // validate mustEqual
    if (!error && entry.mustEqual !== undefined && entry.mustEqual !== null) {
      const currentValue = values[entry.mustEqual];
      const mustEqualField = schema.find(item => item.name === entry.mustEqual);
      error =
        value !== currentValue
          ? ({
              message: 'mustEqual',
              params: {
                name:
                  mustEqualField.errorName || mustEqualField.label || mustEqualField.placeholder || mustEqualField.name,
                count: entry.mustEqual,
                value,
                currentValue,
              },
            } as ValidationError)
          : undefined;
    }

    // validate only if required or has value
    if (entry.required || !validator.isEmpty(value)) {
      // validate by type
      if (!error) {
        switch (entry.type) {
          case 'number':
            if (typeof value === 'number') break;
            error = validator.isNumeric(value) ? undefined : { message: 'numeric' };
            break;
          case 'email':
            error = validator.isString(value) && validator.isEmail(value as string) ? undefined : { message: 'email' };
            break;
        }

        if (error) {
          error.type = entry.type;
        }
      }

      // validate custom validation
      if (!error && entry.customValidation?.length > 0) {
        const { message } = entry.customValidation.find(({ validate }) => validate?.(value)) || {};

        if (message) {
          error = { message, type: 'custom' };
        }
      }
    }

    if (error) {
      // auto translate message if it was auto generated
      if (!entry.requiredMessage && error.type != 'custom') {
        error.message = i18next.t('validation.' + error.message, error.params);
      }
      errors[key] = error;
    }
  });

  return errors;
}
